import {
  MUSIC_COLLECTION,
  SFX_COLLECTION,
  SFX_COLLECTION_WITH_ENGAGEMENT,
} from './collections';
import { parseFilterBy } from './filter-utils';
import { withTrackEngagementFilter } from './track-engagement-join';
import type { SortOption } from './typesense-sort';
import { getSortBy } from './typesense-sort';

// Search pagination constants

// Page sizes for all subsequent pages
export const PREMIUM_PAGE_SIZE = 16;
export const FREE_PAGE_SIZE = 16;

// Have more free results on the first page than premium
// This prevents large groups at the end of the first page
export const FREE_PAGE_1_SIZE = FREE_PAGE_SIZE + 8;
// Work out the required offset for subsequent free pages
export const FREE_OFFSET = FREE_PAGE_1_SIZE - FREE_PAGE_SIZE;

// Typesense ignores `offset` when `page` is also set, so free queries must
// paginate by absolute offset alone (and omit `page`). Page 1 fetches
// FREE_PAGE_1_SIZE hits, so every later page starts FREE_OFFSET beyond a
// page-based window — otherwise the last FREE_OFFSET hits of page 1 repeat
// on page 2 (DEV-5715).
export const getFreeQueryOffset = (page: number) =>
  page === 1 ? 0 : (page - 1) * FREE_PAGE_SIZE + FREE_OFFSET;

export const SINGLE_QUERY_PAGE_SIZE = PREMIUM_PAGE_SIZE + FREE_PAGE_SIZE;

// Free and premium results are only mixed for non-subscribers on the default
// sort. Every other listing is a single query.
export const shouldIntersperse = ({
  sort,
  isSubscribed,
  requested = true,
}: {
  sort: SortOption;
  isSubscribed: boolean;
  requested?: boolean;
}): boolean => (sort === 'relevancy' && !isSubscribed ? requested : false);

// Helper function to determine which SFX collection to use
const getSfxCollection = (contextSlug?: string | false) =>
  contextSlug ? SFX_COLLECTION_WITH_ENGAGEMENT : SFX_COLLECTION;

const MUSIC_QUERY_BY = 'name, keywords, contributor_name, mood, genre, theme';
const MUSIC_QUERY_BY_WEIGHTS =
  'name:2, mood:3, genre:3, theme:3, keywords:2, contributor_name:1';

// Query fields for SFX search - removed 'tags' as it doesn't exist in schema
const SFX_QUERY_BY = 'name, contributor_name, keywords';
const SFX_QUERY_BY_WEIGHTS = 'name:4, contributor_name:3, keywords:2';

const doubleAmpJoin = (...arr: (string | undefined)[]) =>
  arr.filter((item) => Boolean(item)).join(' && ') || undefined;

const slugFilterClause = (contextSlug?: string | false): string | undefined =>
  contextSlug ? `slug:="${contextSlug}"` : undefined;

// Music category/collection pages: membership filter + per-page engagement join.
const musicPageClause = (
  contextSlug: string | false | undefined,
  contextFilter: string | undefined,
  isPremium: boolean
): string | undefined =>
  contextSlug
    ? withTrackEngagementFilter({
        pageFilter: contextFilter,
        slug: contextSlug,
        isPremium,
      })
    : undefined;

export interface MusicTierQueryArgs {
  // The page's membership filter (compiled rule or collection filter).
  // Required whenever `contextSlug` is set.
  contextFilter?: string;
  contextSlug?: string | false;
  filter_by: string;
  isAlternativeFirst?: boolean;
  page?: number;
  perPage?: number;
  q?: string;
  sort: SortOption;
  sortScoreVersion?: 'v1' | 'v2' | 'v3' | 'v4' | 'v5';
}

const buildMusicTierBase = (
  queryText: string,
  collection: string,
  sortBy: string
) => ({
  q: queryText,
  query_by: MUSIC_QUERY_BY,
  query_by_weights: MUSIC_QUERY_BY_WEIGHTS,
  collection,
  sort_by: sortBy,
  exhaustive_search: false,
  drop_tokens_threshold: 0,
  num_typos: 0,
  exclude_fields: 'id',
});

// Premium paginates by `page`.
export const buildPremiumMusicQuery = ({
  q,
  filter_by,
  sort,
  page = 1,
  contextSlug,
  contextFilter,
  sortScoreVersion = 'v1',
  isAlternativeFirst = false,
  perPage,
}: MusicTierQueryArgs) => {
  const { search, filter } = parseFilterBy(filter_by);
  const hasSearchTerms = search.length > 0;
  const collection = MUSIC_COLLECTION;
  const queryText = q || (hasSearchTerms ? search.join(', ') : '*');
  const premiumPerPage = perPage ? Math.floor(perPage / 2) : PREMIUM_PAGE_SIZE;

  return {
    ...buildMusicTierBase(
      queryText,
      collection,
      getSortBy(
        sort,
        true,
        false,
        collection,
        sortScoreVersion,
        !!(q || hasSearchTerms),
        isAlternativeFirst,
        !!contextSlug
      )
    ),
    per_page: premiumPerPage,
    filter_by: doubleAmpJoin(
      filter,
      musicPageClause(contextSlug, contextFilter, true),
      'is_premium:true'
    ),
    page,
  };
};

// Free paginates by absolute `offset` (Typesense ignores `offset` when `page` is set), with a larger first page.
export const buildFreeMusicQuery = ({
  q,
  filter_by,
  sort,
  page = 1,
  contextSlug,
  contextFilter,
  sortScoreVersion = 'v1',
  isAlternativeFirst = false,
  perPage,
}: MusicTierQueryArgs) => {
  const { search, filter } = parseFilterBy(filter_by);
  const hasSearchTerms = search.length > 0;
  const collection = MUSIC_COLLECTION;
  const queryText = q || (hasSearchTerms ? search.join(', ') : '*');
  const isFirstPage = page === 1;
  const defaultFreePerPage = isFirstPage ? FREE_PAGE_1_SIZE : FREE_PAGE_SIZE;
  const freePerPage = perPage ? Math.ceil(perPage / 2) : defaultFreePerPage;
  const freeOffset = perPage
    ? (page - 1) * freePerPage
    : getFreeQueryOffset(page);

  return {
    ...buildMusicTierBase(
      queryText,
      collection,
      getSortBy(
        sort,
        false,
        false,
        collection,
        sortScoreVersion,
        !!(q || hasSearchTerms),
        isAlternativeFirst,
        !!contextSlug
      )
    ),
    per_page: freePerPage,
    offset: freeOffset,
    filter_by: doubleAmpJoin(
      filter,
      musicPageClause(contextSlug, contextFilter, false),
      'is_premium:false'
    ),
  };
};

/**
 * Generates search queries for music based on user input
 */
export const getMusicSearches = ({
  q,
  filter_by,
  sort,
  fetchFreeAndPremium,
  page = 1,
  contextSlug,
  contextFilter,
  sortScoreVersion = 'v1',
  isSubscribedUser = false,
  isAlternativeFirst = false,
  perPage,
}: {
  q?: string;
  filter_by: string;
  sort: SortOption;
  fetchFreeAndPremium: boolean;
  page?: number;
  contextSlug?: string | false;
  contextFilter?: string;
  sortScoreVersion?: 'v1' | 'v2' | 'v3' | 'v4' | 'v5';
  isSubscribedUser?: boolean;
  isAlternativeFirst?: boolean;
  // Optional override for total visible items per page. When set, free/premium
  // splits are calculated from this number and the first-page extra is
  // disabled. Used by surfaces like the contributor page that want a tighter
  // initial load. Defaults to the global PREMIUM/FREE constants when unset.
  perPage?: number;
}) => {
  const { search, filter } = parseFilterBy(filter_by);
  const hasSearchTerms = search.length > 0;
  const collection = MUSIC_COLLECTION;

  if (fetchFreeAndPremium) {
    // searchResults[0] = premium, [1] = free — consumers rely on this order.
    const tierArgs = {
      q,
      filter_by,
      sort,
      page,
      contextSlug,
      contextFilter,
      sortScoreVersion,
      isAlternativeFirst,
      perPage,
    };

    return [buildPremiumMusicQuery(tierArgs), buildFreeMusicQuery(tierArgs)];
  }

  // Check if we're filtering for free tracks specifically
  const isFreeOnly = filter_by.includes('is_premium:false');
  const isPremiumOnly = filter_by.includes('is_premium:true');
  const hasExplicitPremiumFilter = isFreeOnly || isPremiumOnly;

  // Rank by the engagement of the tier being shown, or the viewer's otherwise.
  const isPremiumRanking = hasExplicitPremiumFilter
    ? isPremiumOnly
    : isSubscribedUser;

  const combinedFilter = doubleAmpJoin(
    filter,
    musicPageClause(contextSlug, contextFilter, isPremiumRanking)
  );

  const query = [
    {
      q: q || (hasSearchTerms ? search.join(', ') : '*'),
      query_by: MUSIC_QUERY_BY,
      query_by_weights: MUSIC_QUERY_BY_WEIGHTS,
      collection,
      per_page: perPage ?? SINGLE_QUERY_PAGE_SIZE,
      filter_by: combinedFilter,
      sort_by: getSortBy(
        sort,
        isPremiumRanking,
        false,
        collection,
        sortScoreVersion,
        hasSearchTerms || !!q,
        isAlternativeFirst,
        !!contextSlug
      ),
      exhaustive_search: false,
      drop_tokens_threshold: 0,
      num_typos: 0,
      page,
      exclude_fields: 'id',
    },
  ];

  return query;
};

export interface SfxTierQueryArgs {
  contextSlug?: string | false;
  filter_by: string;
  page?: number;
  perPage?: number;
  q?: string;
  sort: SortOption;
  sortScoreVersion?: 'v1' | 'v2' | 'v3' | 'v4' | 'v5';
}

const buildSfxTierBase = (
  queryText: string,
  collection: string,
  sortBy: string
) => ({
  q: queryText,
  query_by: SFX_QUERY_BY,
  query_by_weights: SFX_QUERY_BY_WEIGHTS,
  collection,
  sort_by: sortBy,
  exhaustive_search: false,
  drop_tokens_threshold: 0,
  num_typos: 0,
  exclude_fields: 'id',
});

// Premium paginates by `page`.
export const buildPremiumSfxQuery = ({
  q,
  filter_by,
  sort,
  page = 1,
  contextSlug,
  sortScoreVersion = 'v1',
  perPage,
}: SfxTierQueryArgs) => {
  const { search, filter } = parseFilterBy(filter_by);
  const hasSearchTerms = search.length > 0;
  const collection = getSfxCollection(contextSlug);
  const queryText = q || (hasSearchTerms ? search.join(', ') : '*');
  const premiumPerPage = perPage ? Math.floor(perPage / 2) : PREMIUM_PAGE_SIZE;

  return {
    ...buildSfxTierBase(
      queryText,
      collection,
      getSortBy(
        sort,
        true,
        false,
        collection,
        sortScoreVersion,
        !!(q || hasSearchTerms)
      )
    ),
    per_page: premiumPerPage,
    filter_by: doubleAmpJoin(
      filter,
      slugFilterClause(contextSlug),
      'is_premium:true'
    ),
    page,
  };
};

// Free paginates by absolute `offset` (Typesense ignores `offset` when `page` is set), with a larger first page.
export const buildFreeSfxQuery = ({
  q,
  filter_by,
  sort,
  page = 1,
  contextSlug,
  sortScoreVersion = 'v1',
  perPage,
}: SfxTierQueryArgs) => {
  const { search, filter } = parseFilterBy(filter_by);
  const hasSearchTerms = search.length > 0;
  const collection = getSfxCollection(contextSlug);
  const queryText = q || (hasSearchTerms ? search.join(', ') : '*');
  const isFirstPage = page === 1;
  const defaultFreePerPage = isFirstPage ? FREE_PAGE_1_SIZE : FREE_PAGE_SIZE;
  const freePerPage = perPage ? Math.ceil(perPage / 2) : defaultFreePerPage;
  const freeOffset = perPage
    ? (page - 1) * freePerPage
    : getFreeQueryOffset(page);

  return {
    ...buildSfxTierBase(
      queryText,
      collection,
      getSortBy(
        sort,
        false,
        false,
        collection,
        sortScoreVersion,
        !!(q || hasSearchTerms)
      )
    ),
    per_page: freePerPage,
    offset: freeOffset,
    filter_by: doubleAmpJoin(
      filter,
      slugFilterClause(contextSlug),
      'is_premium:false'
    ),
  };
};

/**
 * Generates search queries for SFX based on user input
 */
export const getSfxSearches = ({
  q,
  filter_by,
  sort,
  fetchFreeAndPremium,
  page = 1,
  contextSlug,
  sortScoreVersion = 'v1',
  isSubscribedUser = false,
  perPage,
}: {
  q?: string;
  filter_by: string;
  sort: SortOption;
  fetchFreeAndPremium: boolean;
  page?: number;
  contextSlug?: string | false;
  sortScoreVersion?: 'v1' | 'v2' | 'v3' | 'v4' | 'v5';
  isSubscribedUser?: boolean;
  // See getMusicSearches; same semantics for SFX.
  perPage?: number;
}) => {
  const { search, filter } = parseFilterBy(filter_by);
  const hasSearchTerms = search.length > 0;
  const collection = getSfxCollection(contextSlug);

  if (fetchFreeAndPremium) {
    // searchResults[0] = premium, [1] = free — consumers rely on this order.
    const tierArgs = {
      q,
      filter_by,
      sort,
      page,
      contextSlug,
      sortScoreVersion,
      perPage,
    };

    return [buildPremiumSfxQuery(tierArgs), buildFreeSfxQuery(tierArgs)];
  }

  // Check if we're filtering for free tracks specifically
  const isFreeOnly = filter_by.includes('is_premium:false');
  const isPremiumOnly = filter_by.includes('is_premium:true');
  const hasExplicitPremiumFilter = isFreeOnly || isPremiumOnly;

  const combinedFilter = doubleAmpJoin(filter, slugFilterClause(contextSlug));

  const query = [
    {
      q: q || (hasSearchTerms ? search.join(', ') : '*'),
      query_by: SFX_QUERY_BY,
      query_by_weights: SFX_QUERY_BY_WEIGHTS,
      collection,
      per_page: perPage ?? SINGLE_QUERY_PAGE_SIZE,
      filter_by: combinedFilter,
      sort_by: getSortBy(
        sort,
        hasExplicitPremiumFilter ? isPremiumOnly : isSubscribedUser,
        false,
        collection,
        sortScoreVersion,
        hasSearchTerms || !!q
      ),
      exhaustive_search: false,
      drop_tokens_threshold: 0,
      num_typos: 0,
      page,
      exclude_fields: 'id',
    },
  ];

  return query;
};
